import { FormSection } from "@/components/common/inputs/FormSection";
import { getInputProps } from "@/utils/helpers/columnsHelper";
import { Box, Stack } from "@mui/material";
import { TextInput, SelectInput, DateInput, NumberInput, AutocompleteInput, ReferenceInput } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { useEffect, useRef } from "react";
import {
  ResourceName,
  StoplossClaimStatus,
  STOPLOSS_CLAIM_TYPES,
  StoplossClaimType,
  StoplossClaimStatusType,
  ValidAny,
} from "@/types";
import { formatYearRange } from "@/utils/formatters/dateFormatter";

const getStoplossClaimInputProps = (key: string, textReplace = {}) =>
  getInputProps(key, textReplace, ResourceName.STOPLOSS_CLAIMS);

export const StoplossClaimCreateInputs = () => {
  const selectedClientId = useWatch({ name: "client_id_ref" });
  const prevClientIdRef = useRef(selectedClientId);
  const { setValue } = useFormContext();

  // Clear claimant and policy when client changes
  useEffect(() => {
    if (prevClientIdRef.current && prevClientIdRef.current !== selectedClientId) {
      setValue("claimant_id_ref", "");
      setValue("policy_id_ref", "");
    }
    prevClientIdRef.current = selectedClientId;
  }, [selectedClientId, setValue]);

  // Set default empty arrays
  useEffect(() => {
    setValue("source_documents", []);
  }, [setValue]);

  return (
    <Box p={2}>
      {/* Reference Selections */}
      <ReferenceInput source="client_id_ref" reference={ResourceName.CLIENTS} required perPage={100}>
        <AutocompleteInput
          {...getStoplossClaimInputProps("client_id_ref")}
          isRequired
          size="small"
          filterToQuery={(searchText) => ({ q: searchText })}
          optionText="client_legal_name"
        />
      </ReferenceInput>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Box sx={{ flex: 1 }}>
          <ReferenceInput
            source="claimant_id_ref"
            reference={ResourceName.CLAIMANTS}
            required
            filter={{ client_id_ref: selectedClientId }}
            enableGetChoices={({ q }) => !!selectedClientId || !!q}
            perPage={100}
          >
            <AutocompleteInput
              {...getStoplossClaimInputProps("claimant_id_ref")}
              isRequired
              size="small"
              disabled={!selectedClientId}
              filterToQuery={(searchText) => ({ q: searchText })}
              optionText="claimant_name"
            />
          </ReferenceInput>
        </Box>

        <Box sx={{ flex: 1 }}>
          <ReferenceInput
            source="policy_id_ref"
            reference={ResourceName.POLICIES}
            filter={{ client_id_ref: selectedClientId }}
            enableGetChoices={({ q }) => !!selectedClientId || !!q}
            perPage={100}
          >
            <AutocompleteInput
              {...getStoplossClaimInputProps("policy_id_ref")}
              size="small"
              disabled={!selectedClientId}
              filterToQuery={(searchText) => ({ q: searchText })}
              optionText={(record: ValidAny) => {
                const carrier = record.vendor_company_name || record.source_data?.stop_loss_carrier || "Policy";
                const years = formatYearRange(record.effective_start_date, record.effective_end_date);
                return [carrier, years].filter(Boolean).join(" · ");
              }}
            />
          </ReferenceInput>
        </Box>
      </Stack>

      {/* Main Claim Information */}
      <FormSection title="Claim Information" subtitle="Basic details about the stop-loss claim">
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <SelectInput
            {...getStoplossClaimInputProps("stoploss_claim_type")}
            choices={STOPLOSS_CLAIM_TYPES.map((type: StoplossClaimType) => ({
              id: type,
              name: type.toUpperCase(),
            }))}
            required
            fullWidth
            size="small"
          />
          <SelectInput
            {...getStoplossClaimInputProps("claim_status")}
            choices={Object.values(StoplossClaimStatus).map((status: StoplossClaimStatusType) => ({
              id: status,
              name: status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " "),
            }))}
            required
            fullWidth
            size="small"
          />
        </Stack>

        <DateInput {...getStoplossClaimInputProps("submitted_date")} fullWidth size="small" sx={{ mb: 2 }} />

        <TextInput {...getStoplossClaimInputProps("notes")} multiline rows={3} fullWidth size="small" />
      </FormSection>

      {/* Financial Information */}
      <FormSection title="Financial Details" subtitle="Financial amounts and funding information">
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <NumberInput
            {...getStoplossClaimInputProps("total_billed_amount")}
            step={0.01}
            min={0}
            fullWidth
            size="small"
          />
          <NumberInput
            {...getStoplossClaimInputProps("total_allowed_amount")}
            step={0.01}
            min={0}
            fullWidth
            size="small"
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <NumberInput
            {...getStoplossClaimInputProps("estimated_future_expenses")}
            step={0.01}
            min={0}
            fullWidth
            size="small"
          />
          <NumberInput
            {...getStoplossClaimInputProps("total_prior_submissions_ytd")}
            step={0.01}
            min={0}
            fullWidth
            size="small"
          />
        </Stack>

        <NumberInput
          {...getStoplossClaimInputProps("total_amount_accel_funded")}
          step={0.01}
          min={0}
          defaultValue={0}
          fullWidth
          size="small"
        />
      </FormSection>

      {/* System Information */}
      <FormSection title="System Information" subtitle="Source system and administrative details">
        {/* Approve was removed with #7581; a claim is created unapproved and the API default stands. */}
        <TextInput {...getStoplossClaimInputProps("source_system_id")} fullWidth size="small" />
      </FormSection>
    </Box>
  );
};
